import { Position, WidgetType } from '@kemu-io/kemu-core/types';
import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';
import { satisfies } from 'compare-versions';
import { LogicMapperState } from '../logicMapperSlice';
import { defineGroupFieldVariables } from './widgetGroupHelpers';
import { initializeAddedWidgets } from './common/widgetInitialization';
import { WidgetCollectionItem } from '@src/app/reducers/widget/widgetSlice';
import { StatelessRecipeWidget, StatelessWidgetsMap } from '@src/types/core_t';
import * as RecipeActions from '@src/app/recipe/utils';
import { safeJsonClone } from '@common/utils';
import { RootState } from '@src/app/store';

type AddWidgetFromTemplateProps = {
  widget: WidgetCollectionItem,
  recipeId: string,
  thingId: string,
  currentGroupId?: string,
  position?: Position,
}

/**
 * Adds custom widgets and widget bundles to the current recipe
 * from a template. 
 * 
 * NOTE: For bundles, this handles widgets already installed, not
 * newly created widgets, for that checkout `addWidgetBundleFromZip`.
 */
const addWidgetFromTemplateAction = createAsyncThunk(
  '/LogicMapper/addWidgetFromTemplate', async (
  payload: AddWidgetFromTemplateProps,
  thunkApi
) : Promise<StatelessWidgetsMap> => {

  const isV2 = satisfies(payload.widget.protocolVersion || '0', '>=2.0');

	const { addedWidgets, thingWidgets } = await RecipeActions.addCustomWidgetFromTemplate(
		payload.widget,
		payload.recipeId,
		payload.thingId,
		payload.position,
		payload.currentGroupId,
    {
      // Remove inner connections for old widgets.
      removeConnections: !isV2
    }
	);

  // Create a stateless dictionary of the widgets to store in redux
	const stateLessWidgets: StatelessWidgetsMap = {};

  // Initialize newly added widgets
  const addedWidgetsList = Object.values(addedWidgets);
  // The user ID is not directly available in this thunk's payload or state, 
  // so we'll pass undefined. The shared function handles this.
  const { user } = thunkApi.getState() as RootState;

  Object.values(thingWidgets).map((widget) => {
    // Before the state is removed, we need to define the group field variables
    if (widget.type === WidgetType.widgetGroup) {
      // Only re-define variables of the newly added widgets
      if (addedWidgets[widget.id]) {
        defineGroupFieldVariables(widget);
      }
    }

    const { state: _state, ...stateLess } = widget;
    stateLessWidgets[widget.id] = stateLess as StatelessRecipeWidget;
  });

  await initializeAddedWidgets({
    recipeId: payload.recipeId,
    thingId: payload.thingId,
    addedWidgetsList,
    addedWidgetsMap: addedWidgets,
    thunkApi,
    userId: user.currentUserProfile.profile?.id
  });


  const updatedWidgets = safeJsonClone(stateLessWidgets);


  return updatedWidgets;
});


const addWidgetFromTemplateReducer = ((builder: ActionReducerMapBuilder<LogicMapperState>): void => {
  builder.addCase(addWidgetFromTemplateAction.fulfilled, (state, action) => {
    state.gates = action.payload;
  });

  builder.addCase(addWidgetFromTemplateAction.rejected, (_state, action) => {
    console.error('Failed to add widget from template: ', action.error);
  });
});

export default {
  action: addWidgetFromTemplateAction,
  reducer: addWidgetFromTemplateReducer,
};
